import { useState, useMemo, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, ArrowClockwise, Check, PencilSimple, X, Trash, ShieldCheck,
} from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const STR_FIELDS = ["objective", "audience", "core_problem", "core_message", "offer",
  "funnel_stage", "lead_magnet", "measurement_plan", "testing_plan"];
const LIST_FIELDS = ["recommended_channels", "content_pillars", "content_topics"];

const LABELS = {
  objective: "Objective",
  audience: "Audience",
  core_problem: "Core problem",
  core_message: "Core message",
  offer: "Offer",
  funnel_stage: "Funnel stage",
  recommended_channels: "Recommended channels",
  content_pillars: "Content pillars",
  content_topics: "Content topics",
  lead_magnet: "Lead magnet / CTA idea",
  measurement_plan: "Measurement plan",
  testing_plan: "Testing plan",
};

const SECTIONS = [
  { key: "overview", label: "Strategy Overview",
    fields: ["objective", "audience", "core_problem", "core_message", "offer", "funnel_stage"] },
  { key: "content", label: "Content Plan",
    fields: ["recommended_channels", "content_pillars", "content_topics", "lead_magnet"] },
  { key: "measurement", label: "Measurement & Testing",
    fields: ["measurement_plan", "testing_plan"] },
];

function StatusPill({ status }) {
  if (status === "approved") return (
    <span data-testid="strategy-status-pill" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-400/30 bg-emerald-400/5 text-emerald-200">
      <ShieldCheck weight="bold" className="h-3 w-3" /> Approved
    </span>
  );
  if (status === "draft") return (
    <span data-testid="strategy-status-pill" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/5 text-amber-200">
      <PencilSimple weight="bold" className="h-3 w-3" /> Draft — needs approval
    </span>
  );
  return null;
}

function StrList({ items }) {
  if (!items || items.length === 0) return <span className="text-ink-secondary/60 italic">—</span>;
  return (
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-ink-primary leading-relaxed pl-3 relative">
          <span className="absolute left-0 top-2 h-1 w-1 rounded-full bg-white/40" />
          {it}
        </li>
      ))}
    </ul>
  );
}

export default function CampaignStrategy({ campaign, onCampaignUpdated }) {
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftEdits, setDraftEdits] = useState(null);  // local edits before save

  const status = campaign?.strategy_status || "none";
  const strategy = campaign?.strategy || null;

  // Sync local edits when campaign changes
  useEffect(() => {
    if (!editing) setDraftEdits(null);
  }, [campaign?.strategy, campaign?.strategy_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const view = useMemo(() => {
    if (editing && draftEdits) return draftEdits;
    return strategy || {};
  }, [editing, draftEdits, strategy]);

  const doGenerate = async (confirmOverwriteApproved = false) => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.campaign_id}/strategy/generate`,
        { confirm_overwrite_approved: !!confirmOverwriteApproved });
      onCampaignUpdated(data);
      toast.success("Strategy draft generated — review and approve when ready.");
      setEditing(false); setDraftEdits(null);
    } catch (err) {
      if (err?.response?.status === 409) {
        setConfirmRegen(true);
      } else {
        toast.error(err?.response?.data?.detail || "Generation failed. Try again.");
      }
    } finally { setGenerating(false); }
  };

  const doApprove = async () => {
    // Save any pending edits first
    if (editing && draftEdits) {
      const saved = await savePendingEdits();
      if (!saved) return;
    }
    setApproving(true);
    try {
      const { data } = await api.post(`/campaigns/${campaign.campaign_id}/strategy/approve`);
      onCampaignUpdated(data);
      toast.success("Strategy approved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approve failed");
    } finally { setApproving(false); }
  };

  const savePendingEdits = async () => {
    if (!draftEdits) return true;
    try {
      const payload = {};
      for (const k of STR_FIELDS) if (typeof draftEdits[k] === "string") payload[k] = draftEdits[k];
      for (const k of LIST_FIELDS) if (Array.isArray(draftEdits[k])) payload[k] = draftEdits[k];
      const { data } = await api.patch(`/campaigns/${campaign.campaign_id}/strategy`, payload);
      onCampaignUpdated(data);
      setEditing(false); setDraftEdits(null);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
      return false;
    }
  };

  const startEditing = () => { setDraftEdits({ ...strategy }); setEditing(true); };
  const cancelEditing = () => { setDraftEdits(null); setEditing(false); };

  const setField = (k, v) => setDraftEdits(prev => ({ ...(prev || strategy || {}), [k]: v }));

  const doClear = async () => {
    try {
      const { data } = await api.delete(`/campaigns/${campaign.campaign_id}/strategy`);
      onCampaignUpdated(data);
      setEditing(false); setDraftEdits(null);
      toast.success("Strategy cleared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear strategy");
    } finally { setConfirmClear(false); }
  };

  // -------- Empty state --------
  if (status === "none") {
    return (
      <div className="text-center py-12 px-6 border border-dashed border-white/10 rounded-md bg-graphite-950/40" data-testid="strategy-empty">
        <Sparkle weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/50" />
        <div className="mt-4 font-display text-xl tracking-tight">Turn your brief into a strategy</div>
        <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
          Let AI draft a starting-point strategy from the campaign brief — objective, audience, message, channels,
          content pillars, measurement and testing. You review, edit, then approve before it's saved as the official plan.
        </p>
        <p className="mt-2 text-[11px] text-ink-secondary/60 max-w-md mx-auto italic">
          Landing pages, email/WhatsApp nurture, ads and video concepts are handled by separate systems — not included in this draft.
        </p>
        <button data-testid="strategy-generate-btn" onClick={() => doGenerate(false)} disabled={generating}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-5 py-2.5 text-sm font-medium hover:bg-neutral-200 disabled:opacity-60">
          <Sparkle weight="bold" className="h-4 w-4" />
          {generating ? "Generating…" : "Generate strategy"}
        </button>
      </div>
    );
  }

  // -------- Draft or Approved: rendered strategy --------
  return (
    <div className="space-y-6" data-testid="strategy-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-white/10">
        <div className="flex items-center gap-3">
          <StatusPill status={status} />
          {campaign.strategy_generated_at && (
            <span className="text-[11px] text-ink-secondary/70">
              Generated {new Date(campaign.strategy_generated_at).toLocaleString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!editing && (
            <button data-testid="strategy-edit-btn" onClick={startEditing}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5">
              <PencilSimple weight="bold" className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {editing && (
            <>
              <button data-testid="strategy-cancel-edit-btn" onClick={cancelEditing}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5">
                <X weight="bold" className="h-3.5 w-3.5" /> Cancel edits
              </button>
              <button data-testid="strategy-save-edit-btn" onClick={savePendingEdits}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs text-ink-primary hover:bg-white/15">
                <Check weight="bold" className="h-3.5 w-3.5" /> Save edits
              </button>
            </>
          )}
          <button data-testid="strategy-regenerate-btn" onClick={() => doGenerate(false)} disabled={generating}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-60">
            <ArrowClockwise weight="bold" className="h-3.5 w-3.5" /> {generating ? "Regenerating…" : "Regenerate"}
          </button>
          {status === "draft" && (
            <button data-testid="strategy-approve-btn" onClick={doApprove} disabled={approving}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400 text-graphite-950 px-3.5 py-1.5 text-xs font-medium hover:bg-emerald-300 disabled:opacity-60">
              <ShieldCheck weight="bold" className="h-3.5 w-3.5" /> {approving ? "Approving…" : "Approve"}
            </button>
          )}
          <button data-testid="strategy-clear-btn" onClick={() => setConfirmClear(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary/70 hover:text-accent-danger hover:bg-white/5">
            <Trash weight="bold" className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {SECTIONS.map(sec => (
        <section key={sec.key} data-testid={`strategy-section-${sec.key}`} className="rounded-md border border-white/10 bg-graphite-950/40 p-5">
          <h4 className="text-[11px] uppercase tracking-[0.24em] text-ink-secondary/80 mb-4">{sec.label}</h4>
          <dl className="space-y-4">
            {sec.fields.map(fk => {
              const isList = LIST_FIELDS.includes(fk);
              const value = view[fk];
              return (
                <div key={fk} data-testid={`strategy-field-${fk}`} className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 md:gap-6">
                  <dt className="text-xs uppercase tracking-[0.14em] text-ink-secondary pt-1">{LABELS[fk]}</dt>
                  <dd>
                    {editing ? (
                      isList ? (
                        <Textarea data-testid={`strategy-input-${fk}`}
                          value={(value || []).join("\n")}
                          onChange={e => setField(fk, e.target.value.split("\n").map(s => s.trim()).filter(Boolean))}
                          placeholder="One item per line"
                          rows={Math.max(3, (value || []).length + 1)}
                          className="bg-graphite-900 border-white/10 text-sm" />
                      ) : (
                        (["objective", "audience", "core_problem", "core_message", "offer", "funnel_stage", "lead_magnet"].includes(fk) && (value || "").length < 120) ? (
                          <Input data-testid={`strategy-input-${fk}`} value={value || ""}
                            onChange={e => setField(fk, e.target.value)}
                            className="bg-graphite-900 border-white/10 text-sm" />
                        ) : (
                          <Textarea data-testid={`strategy-input-${fk}`} value={value || ""}
                            onChange={e => setField(fk, e.target.value)} rows={3}
                            className="bg-graphite-900 border-white/10 text-sm" />
                        )
                      )
                    ) : (
                      isList ? (
                        <StrList items={value || []} />
                      ) : (
                        <p className="text-sm text-ink-primary leading-relaxed whitespace-pre-wrap">
                          {value || <span className="text-ink-secondary/60 italic">—</span>}
                        </p>
                      )
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}

      {/* Confirm regenerate on approved */}
      <AlertDialog open={confirmRegen} onOpenChange={setConfirmRegen}>
        <AlertDialogContent data-testid="strategy-confirm-regen-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate an approved strategy?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              This campaign already has an <strong>approved</strong> strategy. Regenerating replaces it with a new AI draft
              and reverts the status back to <em>draft</em> — you'll need to review and approve again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-confirm-regen-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Keep approved</AlertDialogCancel>
            <AlertDialogAction data-testid="strategy-confirm-regen-ok" onClick={() => { setConfirmRegen(false); doGenerate(true); }}
              className="bg-amber-400 text-graphite-950 hover:bg-amber-300">Yes, regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm clear strategy */}
      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent data-testid="strategy-confirm-clear-dialog" className="bg-graphite-900 border-white/10 text-ink-primary">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this strategy?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary">
              Removes the AI-generated / approved strategy from this campaign. The campaign brief itself is untouched — you can regenerate any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="strategy-confirm-clear-cancel" className="bg-graphite-800 border-white/10 text-ink-primary hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="strategy-confirm-clear-ok" onClick={doClear}
              className="bg-red-400/90 text-graphite-950 hover:bg-red-300">Clear strategy</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
